import { supabase } from '@/integrations/supabase/client';
import { toEdgeFunctionFailure } from '@/utils/userFacingError';
import { AccountingMethodResolution } from '@/types/fiscalYear';

export type SieImportResult = {
  importFileId: string;
  status: 'completed' | 'failed';
  importedCount: number;
  errorCount: number;
  errorSummary: { total: number; no_mapping: number; imbalance: number; parse_error: number };
};

export type SieFilePreview = {
  chartType: string | null;
  companyName: string | null;
  fiscalYears: Array<{ yearId: number; startDate: string; endDate: string }>;
  verificationCount: number;
  isCurrentYear: boolean;
  conflictError?: string;
  // Only present when companyId was supplied and a primary (#RAR 0) fiscal year
  // was found in the file — see specs/sie-accounting-method-detection/requirements.md.
  accountingMethod?: AccountingMethodResolution;
};

export async function previewSieFile(file: File, companyId?: string): Promise<SieFilePreview> {
  const formData = new FormData();
  formData.append('action', 'previewSieFile');
  formData.append('file', file);
  if (companyId) formData.append('companyId', companyId);

  const { data, error } = await supabase.functions.invoke('sie4-api', { body: formData });

  if (error) {
    throw await toEdgeFunctionFailure(error, 'Preview failed');
  }
  if (data?.error) throw new Error(data.message ?? data.error);

  return data as SieFilePreview;
}

export async function importSieFile(
  companyId: string,
  file: File,
  accountingMethod: 'accrual' | 'cash',
  documentIds?: string[]
): Promise<SieImportResult> {
  const formData = new FormData();
  formData.append('action', 'importSieFile');
  formData.append('companyId', companyId);
  formData.append('file', file);
  formData.append('accounting_method', accountingMethod);
  if (documentIds && documentIds.length > 0) {
    formData.append('document_ids', JSON.stringify(documentIds));
  }

  const { data, error } = await supabase.functions.invoke('sie4-api', { body: formData });

  if (error) {
    throw await toEdgeFunctionFailure(error, 'Import failed');
  }
  if (data?.error) throw new Error(data.message ?? data.error);

  return data as SieImportResult;
}

export async function uploadSieDocument(companyId: string, file: File): Promise<string> {
  const formData = new FormData();
  formData.append('action', 'uploadSieDocument');
  formData.append('companyId', companyId);
  formData.append('file', file);

  const { data, error } = await supabase.functions.invoke('sie4-api', { body: formData });

  if (error) {
    throw await toEdgeFunctionFailure(error, 'Upload failed');
  }
  if (data?.error) throw new Error(data.message ?? data.error);

  return (data as { sieAttachmentId: string }).sieAttachmentId;
}
